<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ArrowRight, Link2, Plus, Trash2 } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import {
  TURTLE_KEYS,
  addTurtleEntry,
  normalizeTurtleData,
  removeTurtleEntry,
  toggleTurtleProcess,
  updateTurtleEntry,
  type TurtleEntry,
  type TurtleKey,
  type TurtleProcessLink,
} from '@/utils/processTurtle'

const props = defineProps<{ modelValue: any; process?: any; processes: any[]; readonly?: boolean }>()
const emit = defineEmits<{
  (event: 'update:modelValue', value: any): void
  (event: 'open-links', links: TurtleProcessLink[]): void
}>()
const { t } = useI18n()
const linkingEntry = ref('')
const searches = ref<Record<string, string>>({})
const sectionLayout: Record<TurtleKey, string> = {
  inputs: 'order-2 md:col-start-1 md:row-start-2',
  outputs: 'order-3 md:col-start-3 md:row-start-2',
  responsibilities: 'order-4 md:col-start-1 md:row-start-1',
  resources: 'order-5 md:col-start-3 md:row-start-1',
  methods: 'order-6 md:col-start-1 md:row-start-3',
  kpis: 'order-7 md:col-start-3 md:row-start-3',
}

function resizeTextArea(element: HTMLTextAreaElement) {
  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}

const vAutoResize = {
  mounted: resizeTextArea,
  updated: resizeTextArea,
}

function entries(key: TurtleKey): TurtleEntry[] {
  return normalizeTurtleData(props.modelValue)[key]
}

function entryKey(key: TurtleKey, index: number) {
  return `${key}:${index}`
}

function updateText(key: TurtleKey, index: number, text: string | number) {
  emit('update:modelValue', updateTurtleEntry(normalizeTurtleData(props.modelValue), key, index, { text: String(text) }))
}

function addEntry(key: TurtleKey) {
  emit('update:modelValue', addTurtleEntry(normalizeTurtleData(props.modelValue), key))
}

function removeEntry(key: TurtleKey, index: number) {
  emit('update:modelValue', removeTurtleEntry(normalizeTurtleData(props.modelValue), key, index))
  linkingEntry.value = ''
}

function toggleProcess(key: TurtleKey, index: number, processId: number) {
  emit('update:modelValue', toggleTurtleProcess(normalizeTurtleData(props.modelValue), key, index, processId))
}

function processFor(id: number) {
  return props.processes.find((process) => process.id === id)
}

function linksFor(entry: TurtleEntry): TurtleProcessLink[] {
  if (entry.linked_processes?.length) return entry.linked_processes
  return entry.process_ids.map((id) => {
    const process = processFor(id)
    return {
      process_id: id,
      code: process?.code,
      title: process?.title,
      category: process?.category,
      release_number: process?.release_number,
    }
  })
}

function filteredProcesses(key: TurtleKey, index: number) {
  const term = (searches.value[entryKey(key, index)] || '').trim().toLowerCase()
  if (!term) return props.processes
  return props.processes.filter((process) => `${process.code} ${process.title}`.toLowerCase().includes(term))
}
</script>

<template>
  <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(13rem,0.9fr)_minmax(0,1fr)] md:grid-rows-[auto_1fr_auto] md:gap-x-8 md:gap-y-5">
    <section
      v-for="key in TURTLE_KEYS"
      :key="key"
      class="rounded-xl border bg-slate-50 p-3 shadow-sm"
      :class="sectionLayout[key]"
    >
      <div class="mb-3 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-slate-800">{{ t(`processManagement.turtle.${key}`) }}</h3>
        <Button v-if="!readonly" type="button" size="sm" variant="ghost" @click="addEntry(key)"><Plus class="mr-1 h-4 w-4" />{{ t('processManagement.addEntry') }}</Button>
      </div>

      <div class="space-y-3">
        <article
          v-for="(entry, index) in entries(key)"
          :key="`${key}-${index}`"
          class="rounded-lg border bg-white p-3"
          :class="{ 'cursor-pointer hover:border-[#80BA27]': readonly && linksFor(entry).length }"
          @click="readonly && linksFor(entry).length && emit('open-links', linksFor(entry))"
        >
          <div v-if="!readonly" class="flex items-start gap-2">
            <textarea
              v-auto-resize
              :value="entry.text"
              rows="1"
              class="min-h-9 w-full resize-none overflow-hidden rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-5 shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              :placeholder="t('processManagement.entryText')"
              @input="updateText(key, index, ($event.target as HTMLTextAreaElement).value)"
            />
            <Button type="button" size="icon" variant="outline" :title="t('processManagement.linkProcesses')" @click="linkingEntry = linkingEntry === entryKey(key, index) ? '' : entryKey(key, index)"><Link2 class="h-4 w-4" /></Button>
            <Button type="button" size="icon" variant="ghost" class="text-red-600" :title="t('processManagement.removeEntry')" @click="removeEntry(key, index)"><Trash2 class="h-4 w-4" /></Button>
          </div>
          <p v-else class="text-sm text-slate-800">{{ entry.text }}</p>

          <div v-if="linksFor(entry).length" class="mt-2 flex flex-wrap gap-1">
            <button
              v-for="link in linksFor(entry)"
              :key="`${link.process_id}-${link.release_id || 'current'}`"
              type="button"
              class="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
              @click.stop="emit('open-links', [link])"
            >
              {{ link.code || `#${link.process_id}` }} · {{ link.title || t('processManagement.unavailableProcess') }}
              <span v-if="link.release_number">· v{{ link.release_number }}</span>
            </button>
          </div>

          <div v-if="!readonly && linkingEntry === entryKey(key, index)" class="mt-3 rounded-lg border bg-slate-50 p-2" @click.stop>
            <Input v-model="searches[entryKey(key, index)]" class="mb-2" :placeholder="t('processManagement.searchProcesses')" />
            <div class="max-h-48 space-y-1 overflow-y-auto">
              <label v-for="process in filteredProcesses(key, index)" :key="process.id" class="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-white">
                <input type="checkbox" :checked="entry.process_ids.includes(process.id)" @change="toggleProcess(key, index, process.id)" />
                <span class="font-semibold">{{ process.code }}</span><span>{{ process.title }}</span>
                <span class="ml-auto text-slate-400">{{ process.released ? `v${process.release_number}` : t('processManagement.draft') }}</span>
              </label>
              <p v-if="!filteredProcesses(key, index).length" class="p-2 text-xs text-slate-500">{{ t('processManagement.noLinkTargets') }}</p>
            </div>
          </div>
        </article>
        <p v-if="!entries(key).length" class="py-4 text-center text-xs text-slate-400">{{ t('processManagement.noTurtleEntries') }}</p>
      </div>
    </section>

    <section class="relative order-1 flex min-h-44 flex-col items-center justify-center overflow-visible rounded-[2.5rem] border-2 border-[#80BA27] bg-gradient-to-br from-[#f4f9eb] to-emerald-50 p-6 text-center shadow-sm md:col-start-2 md:row-start-2">
      <ArrowRight class="absolute -left-8 top-1/2 hidden h-6 w-8 -translate-y-1/2 text-[#80BA27] md:block" aria-hidden="true" />
      <ArrowRight class="absolute -right-8 top-1/2 hidden h-6 w-8 -translate-y-1/2 text-[#80BA27] md:block" aria-hidden="true" />
      <div class="text-xs font-semibold uppercase tracking-widest text-emerald-700">{{ t('processManagement.nodeTypes.process') }}</div>
      <div v-if="process?.code" class="mt-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-600">{{ process.code }}</div>
      <h3 class="mt-2 text-lg font-semibold leading-tight text-slate-900">{{ process?.title || '—' }}</h3>
      <p v-if="process?.objective" class="mt-3 line-clamp-4 text-sm leading-5 text-slate-600">{{ process.objective }}</p>
    </section>
  </div>
</template>
